import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "@vilocnv/allsetra-core";
import { ActivationPayment, ActivationPortal } from "app/data/services";
import { toSupportedPaymentMethods } from "app/data/helpers/activationPaymentHelpers";
import { IActivationPaymentMethod } from "app/data/types";

export const getInstallationThunk = createAsyncThunk(
  "activationPortal/getInstallationThunk",
  async ({ activationCode }: { activationCode: string }) => {
    try {
      const response = await ActivationPortal.getInstallation(activationCode);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getSubscriptionThunk = createAsyncThunk(
  "activationPortal/getSubscriptionThunk",
  async ({ activationCode }: { activationCode: string }) => {
    try {
      const response = await ActivationPortal.getSubscriptions(activationCode);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getInformationSecurityQuestionsThunk = createAsyncThunk(
  "activationPortal/getInformationSecurityQuestionsThunk",
  async () => {
    try {
      const response = await ActivationPortal.getInformationSecurityQuestions();

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const activateSubscriptionThunk = createAsyncThunk(
  "activationPortal/activateSubscriptionThunk",
  async (data: any) => {
    try {
      const response = await ActivationPortal.activateSubscription(data);

      if (response.status === 202) {
        toast.success("Subscription has been activated");
      }

      return response;
    } catch (e: any) {
      toast.error("Subscription could not be activated");
      console.error(e);
      throw new Error(e);
    }
  }
);

/**
 * Fetches the payment method catalog once, on entering the payment step.
 * Eligibility is then evaluated locally against the entered IBAN, so editing
 * the field costs no requests.
 *
 * Narrowing to codes this client implements happens here rather than in the
 * component, so the store never holds a method nothing can render.
 */
export const getPaymentMethodsThunk = createAsyncThunk<
  IActivationPaymentMethod[],
  { activationCode: string }
>(
  "activationPortal/getPaymentMethodsThunk",
  async ({ activationCode }, { rejectWithValue }) => {
    try {
      const response =
        await ActivationPayment.getPaymentMethods(activationCode);

      if (response.status === 200) {
        return toSupportedPaymentMethods(response.data?.methods ?? []);
      }

      return rejectWithValue("Unexpected response fetching payment methods");
    } catch (e: any) {
      console.error(e);
      return rejectWithValue(
        e?.response?.data?.message || "Could not load payment methods"
      );
    }
  }
);

export const getAllUserAccountsThunk = createAsyncThunk(
  "activationPortal/getAllUserAccountsThunk",
  async () => {
    try {
      const response = await ActivationPortal.getMyAccounts();
      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);
